import React, { useState, useImperativeHandle } from "react";
import { Form, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router";
import {
  V1AccountResource,
  useV2CreateDocumentTemplateMutation,
  useV2GetFilenameFormatOptionsQuery,
  useV2UpdateDocumentTemplateMutation,
} from "@generated";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import { handleAlert, snakeToText } from "@utils/helperFunctions";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { DocumentTemplate } from "../types";
import FormTextField from "@components/Forms/FormTextField";
import { OptionType } from "../../../VendorQuotesV2/types/Quote";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import { FileUploadDragAndDrop } from "@components/FormsV2/FileUploadDragAndDrop";
import { MultiValue } from "react-select";
import "./style.css";
import CreatableSelect from "react-select/creatable";
import ControlledDropDownV2 from "@components/Forms/ControlledDropDownV2";
import { MultiSelectAutocomplete } from "@components/FormsV2/MultiSelectAutoComplete";
import { useListTeamsForAccount } from "../../ProfessionalServices/common/api";
import useOauth from "@utils/customHooks/useOauth";

interface Props {
  documentTemplateProp?: DocumentTemplate;
  account: {
    data: V1AccountResource;
  };
  permission: string;
  refetch: () => void;
}

const DocumentTemplateForm = React.forwardRef(
  ({ documentTemplateProp, account, permission, refetch }: Props, ref) => {
    // ENV
    const apiHost = process.env.REACT_APP_SCOPESTACK_API_HOST;
    const [accessToken] = useOauth();

    // Max file size increased to ~ 5GB direct upload instead of Base64 encoding
    const MAX_FILE_SIZE = 5 * 1024 * 1024 * 1024;

    // Get Filename Format Options (v2 infers the account from the auth token)
    const { data: filenameFormats } = useV2GetFilenameFormatOptionsQuery();
    const { teams, isLoading } = useListTeamsForAccount();

    // Location States / React Router
    const location = window.location.href;
    const lastWordInLocation = location.substr(location.lastIndexOf("/") + 1);
    var locationArr = location.split("/");
    var id = locationArr[locationArr.length - 1];
    let navigate = useNavigate();

    // Mutations
    const [createDocumentTemplate] = useV2CreateDocumentTemplateMutation();
    const [updateDocumentTemplate] = useV2UpdateDocumentTemplateMutation();

    useImperativeHandle(ref, () => ({
      saveDocumentTemplate,
    }));

    // Alert States
    const [errorMessages, setErrorMessages] = useState<string | string[]>(
      "Something went wrong! Your changes could not be saved at this time."
    );
    const [showFailAlert, setShowFailAlert] = useState(false);
    const [successMessage, setSuccessMessage] = useState(
      "Document Template saved successfully!"
    );
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);

    // Form Field States
    const [templateName, setTemplateName] = useState(
      documentTemplateProp?.name || ""
    );
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [selectedFilename, setSelectedFilename] =
      useState<null | MultiValue<OptionType>>(
        documentTemplateProp?.filenameFormat?.[0]?.split(",").map((item) => {
          return { label: snakeToText(item), value: item };
        }) || null
      );
    const [typedFilename, setTypedFilename] = useState<undefined | string>(
      undefined
    );
    const [mergeTemplateFilename, setMergeTemplateFilename] = useState<string>(
      documentTemplateProp?.mergeTemplateFilename || ""
    );

    const [templateFormat, setTemplateFormat] = useState<string>(
      documentTemplateProp?.templateFormat || "v2"
    );
    const templateFormatOptions = ["v1", "v2"];

    const [includeFormatting, setIncludeFormatting] = useState<boolean>(
      !!documentTemplateProp?.includeFormatting &&
        documentTemplateProp?.format === "tag_template"
    );

    const [mergeFormat, setMergeFormat] = useState<string>(
      documentTemplateProp?.format || "word_template"
    );
    const mergeFormatOptions = [
      { label: "Mail Merge Template (Legacy)", value: "word_template" },
      { label: "Tag Template", value: "tag_template" },
    ];
    const [assignedTeams, setAssignedTeams] = useState<
      {
        value: string;
        label: string;
      }[]
    >(documentTemplateProp?.teams || []);

    const validateForm = () => {
      let isValid = true;
      if (templateName === "") {
        setErrorMessages(
          "You must add a template name before you can save the document template!"
        );
        setShowFailAlert(true);
        isValid = false;
      }
      if (
        (!documentTemplateProp && uploadedFile === null) ||
        (uploadedFile === null && mergeTemplateFilename === "")
      ) {
        setErrorMessages(
          "You must upload a file before you can save the document template!"
        );
        setShowFailAlert(true);
        isValid = false;
      }
      if (selectedFilename === null) {
        setErrorMessages(
          "You must select a filename before you can save the document template!"
        );
        setShowFailAlert(true);
        isValid = false;
      }
      return isValid;
    };

    const saveDocumentTemplate = async () => {
      const formIsValid = validateForm();
      if (!formIsValid) return;

      let documentTemplateData: any = {
        type: "document-templates",
        id,
        attributes: {
          name: templateName,
          format: mergeFormat,
          "filename-format": selectedFilename?.map(
            (filename) => filename.value
          ),
          "merge-template-filename": mergeTemplateFilename,
          "template-format": templateFormat,
          "include-formatting": includeFormatting,
          teams: assignedTeams.map((t) => {
            return { id: t.value, name: t.label };
          }),
        },
        relationships: {
          account: {
            data: {
              type: "accounts",
              id: account?.data?.attributes?.["account-id"],
            },
          },
        },
      };

      try {
        let templateId: string | number | null = null;

        if (lastWordInLocation !== "new") {
          // --- UPDATE ---
          const response = await updateDocumentTemplate({
            id: Number(documentTemplateData.id),
            body: { data: documentTemplateData },
          }).unwrap();

          //@ts-ignore
          templateId = response?.data?.id;
          if (templateId) {
            setSuccessMessage("Document Template updated successfully!");
            setShowSuccessAlert(true);
          }
        } else {
          // --- CREATE ---
          delete documentTemplateData.id;
          const response = await createDocumentTemplate({
            body: { data: documentTemplateData },
          }).unwrap();

          //@ts-ignore
          templateId = response?.data?.id;
          if (templateId) {
            const template = response.data;
            if (!template) return;
            navigate(`/admin/document_templates/edit/${templateId}`, {
              state: {
                new: true,
              },
            });
          }
        }

        // --- UPLOAD FILE if present ---
        if (templateId && uploadedFile) {
          const formData = new FormData();
          formData.append("document_template[merge_template]", uploadedFile);

          fetch(`${apiHost}/v2/document-templates/${templateId}/uploads`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            body: formData,
          })
            .then((res) => {
              if (res?.status !== 201) {
                setErrorMessages(["File upload unsuccessful"]);
                setShowFailAlert(true);
                return;
              }
              refetch();
            })
            .catch((err) => {
              setErrorMessages(["File upload unsuccessful"]);
              setShowFailAlert(true);
            });
        }
      } catch (err: any) {
        let msg = err?.data?.errors?.[0]?.detail;
        setErrorMessages(
          msg ||
            "Something went wrong. Your changes could not be saved at this time."
        );
        setShowFailAlert(true);
      }
    };

    const filenameFormatOptions = filenameFormats?.["filename_format"]?.map(
      (format) => {
        return { label: snakeToText(format), value: format };
      }
    );

    const handleFileSelect = (file: File) => {
      if (!file.type.includes("vnd.openxmlformats")) {
        setErrorMessages(
          "The uploaded file must be an Office compatible document."
        );
        setShowFailAlert(true);
      } else if (!(file.size > MAX_FILE_SIZE)) {
        setUploadedFile(file);
        setMergeTemplateFilename(file.name);
      }
    };

    if (!account || !filenameFormats || isLoading) {
      return <SmallSpinner />;
    }

    return (
      <Form style={{ marginLeft: "16px" }} className="documentTemplateForm">
        {handleAlert(
          showFailAlert,
          errorMessages,
          setShowFailAlert,
          "warning",
          ToastAlert
        )}
        {handleAlert(
          showSuccessAlert,
          successMessage,
          setShowSuccessAlert,
          "success",
          ToastAlert
        )}
        <Row>
          <Col>
            <FormTextField
              label="Name *"
              id="templateName"
              placeholder=""
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              required={true}
              readOnly={permission === "view"}
            />
          </Col>
          <Col>
            <ControlledDropDownV2
              label="Template Format *"
              value={mergeFormat}
              options={mergeFormatOptions.map((opt) => {
                return (
                  <option value={opt.value} key={opt.value}>
                    {opt.label}
                  </option>
                );
              })}
              onChange={(e) => {
                setMergeFormat(e.target.value);
                if (e.target.value === "tag_template") {
                  setTemplateFormat("v2");
                }
              }}
              required={true}
              readOnly={false}
              disabled={false}
            />
          </Col>
          <Col>
            <ControlledDropDownV2
              label="Data Format *"
              value={templateFormat}
              options={templateFormatOptions.map((opt) => {
                return (
                  <option value={opt} key={opt}>
                    {snakeToText(opt)}
                  </option>
                );
              })}
              onChange={(e) => {
                setTemplateFormat(e.target.value);
              }}
              required={true}
              readOnly={mergeFormat === "tag_template"}
              disabled={false}
            />
          </Col>
          <Col>
            <Form.Group
              className="formGroup"
              style={mergeFormat === "tag_template" ? {} : { display: "none" }}
            >
              <Form.Label>Formatting for Tag Templates</Form.Label>
              <Form.Check
                checked={includeFormatting && mergeFormat === "tag_template"}
                readOnly={mergeFormat !== "tag_template"}
                label={
                  "Include date, time, and currency formatted fields for Tag Templates"
                }
                onChange={(e) => setIncludeFormatting(e.target.checked)}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col>
            <label className="labelLike">
              {uploadedFile || mergeTemplateFilename || permission === "view"
                ? "Uploaded Merge Template *"
                : "Upload Merge Template *"}
            </label>
            {uploadedFile || mergeTemplateFilename || permission === "view" ? (
              <div>
                <span>
                  {mergeTemplateFilename}{" "}
                  <FontAwesomeIcon
                    style={{
                      color: "red",
                      cursor: "pointer",
                      marginLeft: "5px",
                    }}
                    icon={faTrashCan}
                    onClick={() => {
                      setUploadedFile(null);
                      setMergeTemplateFilename("");
                    }}
                  />
                </span>
              </div>
            ) : (
              <FileUploadDragAndDrop
                handleFileSelect={handleFileSelect}
                maxFileSize={MAX_FILE_SIZE}
                text={
                  documentTemplateProp
                    ? "Click or drag and drop a file to replace the current template"
                    : undefined
                }
              />
            )}
          </Col>
        </Row>
        <Row style={{ marginTop: "20px" }}>
          <Col>
            <Form.Group className="formGroup">
              <Form.Label>Filename *</Form.Label>
              <p style={{ marginTop: "-4px" }} className="instructionalText14">
                Use the field below to configure how your generated files will
                be named. You may choose standard items that represent project
                values or add custom text by creating your own token.
              </p>
              <CreatableSelect
                onChange={(e) =>
                  setSelectedFilename(e as MultiValue<OptionType>)
                }
                onInputChange={(e) => {
                  setTypedFilename(e);
                }}
                className="basic-single"
                options={filenameFormatOptions}
                isDisabled={permission === "view"}
                placeholder={""}
                isMulti={true}
                value={selectedFilename}
                inputValue={typedFilename}
                onMenuOpen={() => null}
                onMenuClose={() => null}
                isSearchable={true}
                name={typedFilename}
              />
            </Form.Group>
          </Col>
        </Row>
        {selectedFilename ? (
          <Row style={{ marginBottom: "15px" }}>
            <Col>
              <div className="exampleFilename flexCenter">
                <div>
                  <strong>Example Filename: </strong>
                </div>
                &nbsp;
                <div>
                  {selectedFilename
                    .map((filename) => {
                      const foundFilename = filenameFormatOptions.find(
                        (option) => option.value === filename.value
                      );
                      let itemToRender = filename.value;
                      const today = new Date();
                      if (filename.value === "current_time") {
                        itemToRender = today
                          .toLocaleTimeString()
                          .replace(" ", "");
                      } else if (filename.value === "current_date") {
                        itemToRender = today.toJSON().slice(0, 10);
                      } else if (!foundFilename) {
                        itemToRender = filename.value;
                      } else {
                        itemToRender = `my-${filename.value
                          .toString()
                          .replace("_", "-")}`;
                      }
                      return itemToRender;
                    })
                    .join("-")}
                  .docx
                </div>
              </div>
            </Col>
          </Row>
        ) : null}
        <Row>
          <Col>
            <MultiSelectAutocomplete
              label="Assign Teams"
              placeholder="Search by team name or select from list"
              id="serviceTeams"
              options={teams}
              value={assignedTeams}
              onChange={(e) =>
                //@ts-ignore
                setAssignedTeams(e || [])
              }
              isDisabled={
                permission === "view" ||
                (permission === "create" && lastWordInLocation !== "new")
              }
            />
          </Col>
        </Row>
      </Form>
    );
  }
);

export default DocumentTemplateForm;
